import { Routes, Route, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Component, Suspense, lazy, useEffect } from "react";
import type { ReactNode, ErrorInfo } from "react";
import { useAuth } from "./lib/auth";

const WorkerLoginPage     = lazy(() => import("./pages/WorkerLoginPage"));
const WorkerDashboardPage = lazy(() => import("./pages/WorkerDashboardPage"));

function LoadingFallback() {
  return (
    <div style={{
      display: "flex", height: "100vh",
      alignItems: "center", justifyContent: "center",
      background: "rgb(var(--bg))", color: "rgb(var(--fg2))",
      fontFamily: "IBM Plex Mono, monospace", fontSize: 11,
    }}>Yuklanmoqda…</div>
  );
}

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null };
  static getDerivedStateFromError(e: Error) { return { error: e }; }
  componentDidCatch(_e: Error, _info: ErrorInfo) { /* could log to Sentry */ }
  render() {
    if (this.state.error) {
      return (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          height: "100vh", gap: 12, fontFamily: "IBM Plex Mono, monospace",
          background: "rgb(var(--bg))", color: "rgb(var(--fg))",
        }}>
          <div style={{ fontSize: 32 }}>⚠️</div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Xato yuz berdi</div>
          <div style={{ fontSize: 11, color: "rgb(var(--fg2))", maxWidth: 400, textAlign: "center" }}>
            {(this.state.error as Error).message}
          </div>
          <button onClick={() => window.location.reload()}
            style={{ padding: "6px 16px", borderRadius: 7, border: "1px solid var(--border-line2)", background: "transparent", color: "rgb(var(--fg))", cursor: "pointer", fontSize: 12 }}>
            Qayta yuklash
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (user && user.role !== "nazar_worker") return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// Accepts ?access=TOKEN&refresh=TOKEN&user=JSON from hokim-dashboard login
function AuthHandoff() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const access  = params.get("access");
    const refresh = params.get("refresh");
    const userStr = params.get("user");
    if (access && refresh) {
      localStorage.setItem("yrbp-worker-access-token", access);
      localStorage.setItem("yrbp-worker-refresh-token", refresh);
      if (userStr) {
        try { localStorage.setItem("yrbp-worker-user", decodeURIComponent(userStr)); } catch { /* noop */ }
      }
      useAuth.setState({
        access,
        refresh,
        user: userStr
          ? (() => { try { return JSON.parse(decodeURIComponent(userStr)); } catch { return null; } })()
          : null,
      });
    }
    // replace: true removes the token-bearing URL from browser history immediately
    navigate("/worker", { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <LoadingFallback />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/"       element={<Navigate to="/worker" replace />} />
          <Route path="/auth"   element={<AuthHandoff />} />
          <Route path="/login"  element={<WorkerLoginPage />} />
          <Route path="/worker" element={<RequireAuth><WorkerDashboardPage /></RequireAuth>} />
          <Route path="*"       element={<Navigate to="/worker" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
